<template>
  <WdsMenu
    z-index="50"
    placement="bottom-end"
    manual-trigger
    :global="tableGlobals"
    @menu:open="load"
  >
    <template #trigger="{ toggle }">
      <WdsButtonGhost :data-test="`tc-settings-${index}`" @click="toggle">
        <WdsIcon name="overflow-menu-horizontal" :size="16" />
      </WdsButtonGhost>
    </template>

    <div v-if="column && !loading">
      <TrustedContactApprove
        v-if="column.viewerCanApprove"
        :trusted-contact-id="column.id"
        @approved="$emit('approved')"
        @error="$emit('approve-error', $event)"
      >
        <template #default="{ loading: loadingApproval }">
          <WdsMenuOption :disabled="loadingApproval" :data-test="`tc-approve-${index}`">
            <WdsIcon :size="16" name="checkmark" />
            {{ $t('Approve') }}
          </WdsMenuOption>
        </template>
      </TrustedContactApprove>

      <a
        v-if="column.worker.viewerCanOfferJob"
        target="_blank"
        :href="
          laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
            freelancer: column.worker.id,
          })
        "
        data-test="tc-offer-job"
        @click.stop
      >
        <WdsMenuOption>
          <WdsIcon :size="16" name="direct-hire" />
          {{ $t('Create hire offer') }}
        </WdsMenuOption>
      </a>

      <WdsMenuOption
        v-if="column.viewerCanReInvite"
        :data-test="`tc-invite-${index}`"
        @click="$emit('re-invite', column)"
      >
        <WdsIcon :size="16" name="email" />
        {{ $t('Invite to onboard') }}
      </WdsMenuOption>

      <WdsMenuOption
        v-if="column.viewerCanUpdate"
        :data-test="`tc-edit-${index}`"
        @click="$emit('edit', column)"
      >
        <WdsIcon :size="16" name="edit" />
        {{ $t('Edit contact') }}
      </WdsMenuOption>

      <a
        v-if="column.worker.viewerCanContact"
        target="_blank"
        :href="
          laravelNamedRoute(namedRoutes.frontend.user.profile.invite, {
            freelancer: column.worker.id,
          })
        "
      >
        <WdsMenuOption :data-test="`tc-message-${index}`">
          <WdsIcon :size="16" name="chat" />
          {{ $t('Message') }}
        </WdsMenuOption>
      </a>

      <WdsMenuOption
        v-if="column.viewerCanDelete"
        theme="DESTRUCTIVE"
        :data-test="`tc-delete-${index}`"
        @click="$emit('delete', column)"
      >
        <WdsIcon :size="16" name="trash-can" />
        {{ $t('Delete') }}
      </WdsMenuOption>
    </div>
    <div v-else-if="loading" class="tw-px-4 tw-py-2">
      <WdsSpinner indeterminate />
    </div>
  </WdsMenu>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsSpinner,
} from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import TrustedContactApprove from '@/components/Contacts/CompanyFreelancer/TrustedContactApprove.vue'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const TrustedContactActionsDataRequest = graphql(`
  fragment TrustedContactActionsDataRequest on TrustedContact {
    id
    viewerCanApprove(account: $companyId)
    viewerCanUpdate(account: $companyId)
    viewerCanDelete(account: $companyId)
    viewerCanReInvite(account: $companyId)
    worker {
      id
      viewerCanContact(account: $companyId)
      viewerCanOfferJob(account: $companyId)
    }
  }
`)

const TrustedContactActionsColumn = graphql(`
  fragment TrustedContactActionsColumn on TrustedContact {
    id
  }
`)

defineEmits<{
  approved: []
  'approve-error': [errors: string[]]
  're-invite': [row: any]
  edit: [row: any]
  delete: [row: any]
}>()

const props = defineProps<{ row: FragmentType<typeof TrustedContactActionsColumn>, index: number, forOrganisation: boolean }>()
const input = computed(() => useFragment(TrustedContactActionsColumn, props.row))

const queryVariables = () => ({ id: input.value.id, companyId: currentAccount.globalId })
const queryOptions = { clientId: 'publicApiCustomFields' }

const { result: companyResult, loading: companyLoading, load: loadCompany } = useLazyQuery(
  graphql(`
    query TrustedContactActionsColumnMenu($id: ID!, $companyId: ID!) {
      trustedContact(id: $id) {
        ...TrustedContactActionsDataRequest
      }
    }
  `),
  queryVariables,
  queryOptions,
)

const { result: orgResult, loading: orgLoading, load: loadOrg } = useLazyQuery(
  graphql(`
    query OrgTrustedContactActionsColumnMenu($id: ID!, $companyId: ID!) {
      organisationTrustedContact(id: $id) {
        ...TrustedContactActionsDataRequest
      }
    }
  `),
  queryVariables,
  queryOptions,
)
const load = () => (props.forOrganisation ? loadOrg() : loadCompany())
const loading = computed(() => companyLoading.value || orgLoading.value)

const column = computed(() => {
  const data = props.forOrganisation
    ? orgResult.value?.organisationTrustedContact
    : companyResult.value?.trustedContact
  return useFragment(TrustedContactActionsDataRequest, data)
})
</script>

<style scoped></style>
